import React, { useEffect, useCallback, useRef, useReducer } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { Dialog, Button } from '@totalsoft/rocket-ui';
import SelectVerificationsComponent from './SelectVerificationsComponent';
import AddVerificationsComponent from './AddVerificationsComponent';
import SummaryComponent from './SummaryComponent';
import { loadVerificationTypes } from 'actions/actionCreators/dictionariesActions';
import {
  addExistingItemToVerificationList,
  removeExistingItemFromVerificationList,
  filterVerificationTypes,
  getVerificationsStatus
} from '../../../common/functions';
import { getVerificationTypesLoaded, getVerificationTypes } from '../../../selectors';
import { generateManualVerifications, loadDocumentEntities } from '../../../actionCreators';
import { emptyArray } from 'constants/emptyConst';
import { useTranslation } from 'react-i18next';
import { emptyString } from 'constants/emptyConst';
import debounce from 'lodash.debounce';
import * as internalActionTypes from '../internalState/actionTypes';
import { reducer as internalReducer } from '../internalState/reducer';
import { BoxActionsWrapper } from 'components/common/CommonStyles';

const Modal = ({
  closePartnerManualInterrogationModal,
  allVerifications,
  documentId,
  siteId,
  partnerId,
  getPagingLimits,
  filterByValue,
  addNewVerifications
}) => {
  const [t] = useTranslation();
  const reduxDispatch = useDispatch();

  const defaultPager = useRef({ page: 1, pageSize: 10 });
  const rowsPerPageOptions = useRef([5, 10, 20]);
  const DEBOUNCE_DELAY = useRef(500);

  const documentEntities = useSelector(state => state.document.entities[`${documentId}-${siteId}`]?.data || emptyArray, shallowEqual);
  const documentEntitiesLoaded = useSelector(state => state.document.entities[`${documentId}-${siteId}`]?.isLoaded || false, shallowEqual);

  const offerPartner = useSelector(state => state.entities?.offerPartners[`${partnerId}`] || null, shallowEqual);

  const [internalState, internalDispatch] = useReducer(internalReducer, {
    addedVerifications: emptyArray,
    verificationEntity: null,
    verificationsToLaunch: emptyArray,
    internalVerifications: allVerifications,
    internalVerificationsOnPage: allVerifications,
    pager: defaultPager.current,
    totalCount: 0,
    filter: emptyString,
    currentEntities: emptyArray
  });

  const composeInternalAction = (type, payload) => ({ type, payload });
  const dispatchInternalAction = useCallback((type, payload) => internalDispatch(composeInternalAction(type, payload)), []);

  const verificationTypes = useSelector(state => getVerificationTypes(state), shallowEqual);
  const verificationsTypeLoaded = useSelector(state => getVerificationTypesLoaded(state), shallowEqual);

  const componentIsMounted = useRef(false);

  useEffect(() => {
    componentIsMounted.current = true;
    return () => {
      componentIsMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (documentEntitiesLoaded) {
      if (offerPartner) {
        const documentEntitiesCopy = JSON.parse(JSON.stringify(documentEntities));

        const currentEntities = documentEntitiesCopy.find(de => de.documentId === documentId && de.siteId === siteId);
        currentEntities.list = currentEntities.list.filter(
          item => item.id === offerPartner?.masterPartnerId || (partnerId && item.id === partnerId && item.partnerCheck?.isOfferPartner)
        );

        dispatchInternalAction(internalActionTypes.CURRENT_ENTITIES, { currentEntities: documentEntitiesCopy });
      } else dispatchInternalAction(internalActionTypes.CURRENT_ENTITIES, { currentEntities: documentEntities });
    }
  }, [offerPartner, documentEntitiesLoaded, dispatchInternalAction, documentEntities, documentId, siteId, partnerId]);

  useEffect(() => {
    if (documentEntitiesLoaded && internalState.currentEntities.length > 0) {
      let verificationsCopy = JSON.parse(JSON.stringify(allVerifications));
      var resultArray = [...emptyArray];
      internalState.currentEntities.forEach(elem => (resultArray = resultArray.concat(...elem.list)));
      verificationsCopy = verificationsCopy.filter(elem =>
        resultArray.some(i => i.name === elem.partnerName || i.UniqueId === elem.EntityUniqueId)
      );

      dispatchInternalAction(internalActionTypes.INTERNAL_VERIFICATIONS, { internalVerifications: verificationsCopy });
    }
  }, [documentEntitiesLoaded, dispatchInternalAction, internalState.currentEntities, allVerifications]);

  //#region Pagination and filtering
  const pushFiltersAndPaging = useCallback(
    (filter, pager) => {
      dispatchInternalAction(internalActionTypes.FILTER, { filter });
      dispatchInternalAction(internalActionTypes.PAGER, { pager });

      const { indexOfFirstItem, indexOfLastItem } = getPagingLimits(pager);

      if (filter !== emptyString) {
        const filteredVerifications = filterByValue(internalState.internalVerifications, filter);
        const currentVerifications = filteredVerifications.slice(indexOfFirstItem, indexOfLastItem);

        dispatchInternalAction(internalActionTypes.INTERNAL_VERIFICATIONS_ON_PAGE, {
          internalVerificationsOnPage: currentVerifications
        });

        dispatchInternalAction(internalActionTypes.TOTAL_COUNT, { totalCount: filteredVerifications.length });
      } else {
        dispatchInternalAction(internalActionTypes.INTERNAL_VERIFICATIONS_ON_PAGE, {
          internalVerificationsOnPage: internalState.internalVerifications.slice(indexOfFirstItem, indexOfLastItem)
        });

        dispatchInternalAction(internalActionTypes.TOTAL_COUNT, {
          totalCount: internalState.internalVerifications.length
        });
      }
    },
    [internalState.internalVerifications, filterByValue, getPagingLimits, dispatchInternalAction]
  );

  useEffect(() => {
    if (internalState.internalVerifications) {
      const pagingLimits = getPagingLimits(internalState.pager);

      dispatchInternalAction(internalActionTypes.INTERNAL_VERIFICATIONS_ON_PAGE, {
        internalVerificationsOnPage: internalState.internalVerifications.slice(pagingLimits.indexOfFirstItem, pagingLimits.indexOfLastItem)
      });

      dispatchInternalAction(internalActionTypes.TOTAL_COUNT, {
        totalCount: internalState.internalVerifications.length
      });
    }
  }, [internalState.internalVerifications, internalState.pager, getPagingLimits, dispatchInternalAction]);

  useEffect(() => {
    pushFiltersAndPaging(internalState.filter, internalState.pager);
  }, [internalState.pager, internalState.filter, pushFiltersAndPaging]);

  const onFilterValueChangedDebounce = _filter => dispatchInternalAction(internalActionTypes.PAGER, { pager: defaultPager.current });
  const filterChangedDebounced = useRef(debounce(onFilterValueChangedDebounce, DEBOUNCE_DELAY.current));

  const handleChangePage = page => {
    const newPager = Object.assign({}, internalState.pager, { page: page + 1, pageSize: internalState.pager.pageSize });
    pushFiltersAndPaging(internalState.filter, newPager);
  };

  const handleChangeRowsPerPage = pageSize => {
    const newPager = Object.assign({}, internalState.pager, { page: 1, pageSize: pageSize });
    pushFiltersAndPaging(internalState.filter, newPager);
  };

  const handleRefresh = () => dispatchInternalAction(internalActionTypes.PAGER, { pager: defaultPager.current });

  const handleFilterValueChanged = (value, debounced) => {
    dispatchInternalAction(internalActionTypes.FILTER, { filter: value });

    if (debounced) filterChangedDebounced.current(value);
    else dispatchInternalAction(internalActionTypes.PAGER, { pager: defaultPager.current });
  };

  const handleResetFilters = () => {
    dispatchInternalAction(internalActionTypes.FILTER, { filter: emptyString });
    dispatchInternalAction(internalActionTypes.PAGER, { pager: defaultPager.current });
  };

  //#endregion

  useEffect(() => {
    if (!documentEntitiesLoaded && componentIsMounted) reduxDispatch(loadDocumentEntities(documentId, siteId));
  }, [documentId, siteId, documentEntitiesLoaded, reduxDispatch, componentIsMounted]);

  useEffect(() => {
    if (!verificationsTypeLoaded && componentIsMounted) reduxDispatch(loadVerificationTypes());
  }, [verificationsTypeLoaded, reduxDispatch, componentIsMounted]);

  const generateVerifications = () => {
    reduxDispatch(generateManualVerifications(internalState.verificationsToLaunch));
    closeModal();
  };

  const resetSelection = () => {
    const newVerificationsOnPage = JSON.parse(JSON.stringify(internalState.internalVerificationsOnPage));
    const newVerifications = JSON.parse(JSON.stringify(internalState.internalVerifications));

    newVerificationsOnPage.forEach(v => (v.isSelected = false));
    newVerifications.forEach(v => (v.isSelected = false));

    dispatchInternalAction(internalActionTypes.INTERNAL_VERIFICATIONS, { internalVerifications: newVerifications });
  };

  const closeModal = () => {
    closePartnerManualInterrogationModal();
    resetSelection();
  };

  const selectVerificationForReinterrogation = useCallback(
    itemIndex => {
      const newVerifications = JSON.parse(JSON.stringify(internalState.internalVerifications));

      newVerifications[itemIndex].isSelected = !newVerifications[itemIndex].isSelected;
      dispatchInternalAction(internalActionTypes.INTERNAL_VERIFICATIONS, { internalVerifications: newVerifications });
    },
    [internalState.internalVerifications, dispatchInternalAction]
  );

  const handleExistingVerificationSelection = (idx, entityName, isSelected) => {
    if (idx === null || idx === undefined) return;
    selectVerificationForReinterrogation(idx);

    const verification = internalState.internalVerifications[idx];
    const { documentId: verificationDocumentId, siteId: verificationSiteId } = verification;

    const verificationTypeId = verificationTypes.find(vt => vt.verificationName === verification.verificationType).verificationTypeId;

    const verificationToAdd = {
      verificationTypeId: verificationTypeId,
      verificationName: verification.verificationType
    };

    const currentEntities = internalState.currentEntities.find(
      oe => oe.documentId === verificationDocumentId && oe.siteId === verificationSiteId
    ).list;

    const entity = currentEntities.find(oe => oe.name === verification.partnerName || oe.UniqueId === verification.entityUniqueId);

    const entityToAdd = { ...entity };

    let newVerificationsToLaunch = JSON.parse(JSON.stringify(internalState.verificationsToLaunch));

    if (!internalState.verificationsToLaunch.some(ver => ver.documentId === verificationDocumentId && ver.siteId === verificationSiteId))
      newVerificationsToLaunch.push({
        documentId: verificationDocumentId,
        siteId: verificationSiteId,
        manualVerifications: [{ entity: entityToAdd, verificationData: [verificationToAdd] }]
      });
    else {
      const element = newVerificationsToLaunch.find(ver => ver.documentId === verificationDocumentId && ver.siteId === verificationSiteId);

      if (!element.manualVerifications.some(e => e?.entity?.id === entityToAdd.id))
        element.manualVerifications.push({ entity: entityToAdd, verificationData: [verificationToAdd] });
      else {
        element.manualVerifications = element.manualVerifications.map(item =>
          item?.entity?.id === entity.id
            ? {
                ...item,
                entity: entityToAdd,
                verificationData: !isSelected
                  ? addExistingItemToVerificationList(item.verificationData, verificationToAdd)
                  : removeExistingItemFromVerificationList(item.verificationData, verificationToAdd)
              }
            : item
        );
      }
    }

    setVerifications(newVerificationsToLaunch, verificationDocumentId, verificationSiteId, entity);
    dispatchInternalAction(internalActionTypes.VERIFICATIONS_TO_LAUNCH, {
      verificationsToLaunch: newVerificationsToLaunch
    });
  };

  const setVerifications = useCallback(
    (verificationsToLaunch, documentId, siteId, entity) => {
      const verificationToLaunch = verificationsToLaunch.find(i => i.documentId === documentId && i.siteId === siteId);
      const manualVerification = verificationToLaunch.manualVerifications.find(i => i.entity.id === entity.id);

      const verificationList = filterVerificationTypes(verificationTypes, entity, internalState.internalVerifications).filter(ver =>
        manualVerification.verificationData.find(v => ver.verificationTypeId === v.verificationTypeId)
      );
      dispatchInternalAction(internalActionTypes.ADDED_VERIFICATIONS, { addedVerifications: verificationList });
    },
    [verificationTypes, dispatchInternalAction, internalState.internalVerifications]
  );

  const handleVerificationsChange = value => {
    const newVerifications = value.map(v => ({
      verificationTypeId: v.verificationTypeId,
      verificationName: v.verificationName
    }));

    const verificationToLaunch = internalState.verificationsToLaunch.find(v => v.documentId === documentId && v.siteId === siteId);

    verificationToLaunch.manualVerifications = verificationToLaunch.manualVerifications.map(item =>
      item?.entity?.id === internalState.verificationEntity.id ? { ...item, verificationData: newVerifications } : item
    );

    internalState.internalVerifications.forEach((v, idx) => {
      const verification = newVerifications.find(nv => nv.verificationName === v.verificationType);
      if (verification && v.partnerName === internalState.verificationEntity.name && !v.isSelected)
        selectVerificationForReinterrogation(idx);
    });

    dispatchInternalAction(internalActionTypes.VERIFICATIONS_TO_LAUNCH, {
      verificationsToLaunch: internalState.verificationsToLaunch
    });
    dispatchInternalAction(internalActionTypes.ADDED_VERIFICATIONS, { addedVerifications: value });
  };

  const handleVerificationEntityChange = value => {
    if (value) {
      const newObject = {
        entity: { ...value },
        verificationData: []
      };
      if (!internalState.verificationsToLaunch.some(i => i.documentId === documentId && i.siteId === siteId)) {
        const newVerification = {
          documentId,
          siteId,
          manualVerifications: [newObject]
        };
        dispatchInternalAction(internalActionTypes.VERIFICATIONS_TO_LAUNCH, {
          verificationsToLaunch: [...internalState.verificationsToLaunch, newVerification]
        });
        dispatchInternalAction(internalActionTypes.ADDED_VERIFICATIONS, { addedVerifications: emptyArray });
      } else {
        const verificationToLaunch = internalState.verificationsToLaunch.find(i => i.documentId === documentId && i.siteId === siteId);

        if (!verificationToLaunch.manualVerifications.some(item => item?.entity?.name === value.name)) {
          verificationToLaunch.manualVerifications.push(newObject);
          dispatchInternalAction(internalActionTypes.VERIFICATIONS_TO_LAUNCH, {
            verificationsToLaunch: internalState.verificationsToLaunch
          });
          dispatchInternalAction(internalActionTypes.ADDED_VERIFICATIONS, { addedVerifications: emptyArray });
        } else setVerifications(internalState.verificationsToLaunch, documentId, siteId, value);
      }
    }
    dispatchInternalAction(internalActionTypes.VERIFICATION_ENTITY, { verificationEntity: value });
  };

  const removeAddedVerification = useCallback(
    (idx, entityId, entityName, verificationName, verificationDocumentId, verificationSiteId) => {
      const verificationToLaunch = internalState.verificationsToLaunch.find(
        v => v.documentId === verificationDocumentId && v.siteId === verificationSiteId
      );

      verificationToLaunch.manualVerifications = verificationToLaunch.manualVerifications.map(item =>
        item?.entity?.id === entityId ? { ...item, verificationData: item.verificationData.filter((_, index) => index !== idx) } : item
      );

      const entity = verificationToLaunch.manualVerifications.find(i => i?.entity?.id === entityId).entity;
      if (entity)
        setVerifications(internalState.verificationsToLaunch, verificationToLaunch.documentId, verificationToLaunch.siteId, entity);

      if (internalState.internalVerifications.find(v => v.partnerName === entityName && v.verificationType === verificationName)) {
        const existingVerificationIndex = internalState.internalVerifications.findIndex(
          v => v.partnerName === entityName && v.verificationType === verificationName
        );
        selectVerificationForReinterrogation(existingVerificationIndex);
      }

      dispatchInternalAction(internalActionTypes.VERIFICATIONS_TO_LAUNCH, {
        verificationsToLaunch: internalState.verificationsToLaunch
      });
    },
    [
      internalState.verificationsToLaunch,
      setVerifications,
      selectVerificationForReinterrogation,
      internalState.internalVerifications,
      dispatchInternalAction
    ]
  );

  const canAddVerifications = useCallback(
    () => internalState.verificationEntity && !!Object.keys(internalState.verificationEntity).length,
    [internalState.verificationEntity]
  );

  return (
    <Dialog
      open={true}
      aria-labelledby='partnerManualInterrogation-dialog-title'
      maxWidth={'lg'}
      disableEscapeKeyDown
      onClose={() => closeModal()}
      content={
        <>
          <SelectVerificationsComponent
            verificationsOnPage={internalState.internalVerificationsOnPage}
            verifications={internalState.internalVerifications}
            handleExistingVerificationSelection={handleExistingVerificationSelection}
            totalCount={internalState.totalCount}
            pageSize={internalState.pager.pageSize}
            page={internalState.pager.page}
            rowsPerPageOptions={rowsPerPageOptions.current}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleChangePage={handleChangePage}
            handleRefresh={handleRefresh}
            filter={internalState.filter}
            handleFilterValueChanged={handleFilterValueChanged}
            handleResetFilters={handleResetFilters}
            entitiesLoaded={documentEntitiesLoaded}
          />
          <div />
          {addNewVerifications && (
            <AddVerificationsComponent
              handleVerificationsChange={handleVerificationsChange}
              addedVerifications={internalState.addedVerifications}
              handleVerificationEntityChange={handleVerificationEntityChange}
              verificationEntity={internalState.verificationEntity}
              documentEntities={internalState.currentEntities}
              canAddVerifications={canAddVerifications}
              verificationTypes={filterVerificationTypes(
                verificationTypes,
                internalState.verificationEntity,
                internalState.internalVerifications
              )}
            />
          )}
          {getVerificationsStatus(internalState.internalVerificationsOnPage, internalState.verificationsToLaunch).status && (
            <SummaryComponent
              removeAddedVerification={removeAddedVerification}
              addedVerificationsToLaunch={internalState.verificationsToLaunch}
            />
          )}
        </>
      }
      actions={
        <BoxActionsWrapper forDialog>
          <Button
            id={'offerPartnerVerification_partnerManualInterrogationModal_launchBtn'}
            variant='contained'
            onClick={() => generateVerifications()}
            disabled={!getVerificationsStatus(internalState.internalVerificationsOnPage, internalState.verificationsToLaunch).status}
          >
            {`${t('Launch')}: ${
              getVerificationsStatus(internalState.internalVerificationsOnPage, internalState.verificationsToLaunch).value
            }`}
          </Button>
          <div />
          <Button id={'offerPartnerVerification_partnerManualInterrogationModal_closeBtn'} variant='contained' onClick={() => closeModal()}>
            {t('Close')}
          </Button>
        </BoxActionsWrapper>
      }
    />
  );
};

Modal.defaultProps = {
  addNewVerifications: true,
  partnerId: null
};

Modal.propTypes = {
  closePartnerManualInterrogationModal: PropTypes.func.isRequired,
  allVerifications: PropTypes.array.isRequired,
  filterByValue: PropTypes.func.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  getPagingLimits: PropTypes.func.isRequired,
  partnerId: PropTypes.number,
  addNewVerifications: PropTypes.bool
};

export default Modal;
